import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Images } from "../../../../assets/images";
import JustAskHero from "./JustAsk";

const slides = [
  {
    img: Images?.LoanDispute,
    title: "Arbitrations & Mediations",
    points: [
      "Online. Hybrid. Physical",
      "Customized workflows for Banks, NBFC, Insurance",
    ],
    primaryButton: "Speak to our Consultant",
  },
  {
    img: Images?.Transcription,
    title: "Live Transcription",
    points: ["Low Latency. High Accuracy", "Edited to Global Standards"],
    primaryButton: "Speak to our Consultant",
  },
  {
    img: Images?.CaseManagement,
    title: "Case Management",
    points: [
      "Tribunal Secretarial Services",
      "Electronic Presentation of Evidence",
      "Bundles",
    ],
    primaryButton: "Speak to our Consultant",
  },
  {
    img: Images?.CloudBasedOdr,
    title: "ODR Platform Deployments",
    points: ["SaaS & On Premise", "Customized workflows"],
    primaryButton: "Speak to our Consultant",
  },
];

const visibleSlides = slides.filter((slide) => !slide.hidden);

/* ── Keyframe animations for background effects ── */
const orbFloat1 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(60px, -40px) scale(1.08); }
  66%       { transform: translate(-30px, 30px) scale(0.96); }
`;
const orbFloat2 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  40%       { transform: translate(-70px, 30px) scale(1.06); }
  70%       { transform: translate(40px, -50px) scale(0.92); }
`;
const orbFloat3 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(30px, 40px) scale(1.1); }
`;
const gridDrift = keyframes`
  0%   { background-position: 0 0; }
  100% { background-position: 48px 48px; }
`;

const ringExpand = keyframes`
  0%   { transform: scale(0.85); opacity: 0.6; }
  100% { transform: scale(1.6);  opacity: 0; }
`;

const HeoBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % visibleSlides.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % visibleSlides.length);
  };

  const goPrev = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + visibleSlides.length) % visibleSlides.length,
    );
  };

  useEffect(() => {
    let touchStartX = 0;
    let isSwiping = false;

    const handleTouchStart = (e) => {
      if (e.target.closest("button")) return;
      touchStartX = e.touches[0].clientX;
      isSwiping = true;
    };

    const handleTouchMove = (e) => {
      if (!isSwiping) return;
      const diffX = Math.abs(touchStartX - e.touches[0].clientX);
      if (diffX > 10) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e) => {
      if (!isSwiping) return;
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          goNext();
        } else {
          goPrev();
        }
      }
      isSwiping = false;
    };

    const banner = document.getElementById("hero-banner");
    if (banner) {
      banner.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      banner.addEventListener("touchmove", handleTouchMove, { passive: false });
      banner.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      if (banner) {
        banner.removeEventListener("touchstart", handleTouchStart);
        banner.removeEventListener("touchmove", handleTouchMove);
        banner.removeEventListener("touchend", handleTouchEnd);
      }
    };
  }, [currentIndex]);

  return (
    <BannerWrapper>
      {/* ── Layer 1: Gradient base ── */}
      <BgBase />

      {/* ── Layer 2: Animated orbs ── */}
      <Orb
        $anim={orbFloat1}
        $size="700px"
        $top="-15%"
        $left="-8%"
        $color="rgba(0,131,140,0.13)"
      />
      <Orb
        $anim={orbFloat2}
        $size="580px"
        $top="30%"
        $left="55%"
        $color="rgba(41,52,97,0.55)"
      />
      <Orb
        $anim={orbFloat3}
        $size="460px"
        $top="60%"
        $left="10%"
        $color="rgba(0,131,140,0.08)"
      />
      <Orb
        $anim={orbFloat1}
        $size="300px"
        $top="-5%"
        $left="70%"
        $color="rgba(0,131,140,0.10)"
        $delay="4s"
      />

      {/* ── Layer 3: Animated grid mesh ── */}
      <GridMesh />

      {/* ── Layer 4: Dot matrix corners ── */}
      <DotMatrix $pos="top-right" />
      <DotMatrix $pos="bottom-left" />

      {/* ── Layer 5: Diagonal light beams ── */}
      {/* <LightBeam $delay="0s" $left="20%" $width="1px" $opacity="0.18" />
      <LightBeam $delay="6s" $left="55%" $width="1.5px" $opacity="0.12" />
      <LightBeam $delay="12s" $left="80%" $width="1px" $opacity="0.10" /> */}

      {/* ── Layer 6: Decorative rings ── */}
      <DecorRing $size="320px" $top="60%" $left="72%" $delay="0s" />
      <DecorRing $size="200px" $top="15%" $left="5%" $delay="2s" />

      {/* ── Layer 7: Horizontal teal accent line ── */}
      {/* <AccentLine /> */}

      {/* ── Layer 8: Vignette edges ── */}
      <Vignette />

      {/* ── Hero content ── */}
      <HeroBanner
        id="hero-banner"
        role="banner"
        aria-label="JustAct Hero Carousel"
      >
        <JustAskHero />
      </HeroBanner>
    </BannerWrapper>
  );
};

export default HeoBanner;

const BannerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 480px;
  max-height: 960px;
  overflow: hidden;

  @media (max-width: 1024px) {
    height: 90vh;
    min-height: 560px;
  }
  @media (max-width: 768px) {
    height: 85vh;
    min-height: 520px;
  }
  @media (max-width: 480px) {
    height: 80vh;
    min-height: 480px;
  }
`;

const BgBase = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    /* top-left teal bloom */
    radial-gradient(
      ellipse 70% 55% at 0% 0%,
      rgba(0, 131, 140, 0.22) 0%,
      transparent 60%
    ),
    /* bottom-right navy bloom */
    radial-gradient(
        ellipse 60% 50% at 100% 100%,
        rgba(20, 28, 68, 0.9) 0%,
        transparent 65%
      ),
    /* centre navy core */
    radial-gradient(
        ellipse 90% 80% at 50% 40%,
        rgba(29, 36, 74, 0.85) 0%,
        transparent 80%
      ),
    /* solid dark navy base */
    linear-gradient(160deg, #1a2245 0%, #0d1a2e 40%, #0a1520 70%, #071218 100%);
`;

const Orb = styled.div`
  position: absolute;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$size};
  height: ${(p) => p.$size};
  border-radius: 50%;
  background: radial-gradient(circle, ${(p) => p.$color} 0%, transparent 70%);
  animation: ${(p) => p.$anim} 20s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || "0s"};
  pointer-events: none;
  filter: blur(2px);
  z-index: 1;

  @media (max-width: 768px) {
    width: calc(${(p) => p.$size} * 0.55);
    height: calc(${(p) => p.$size} * 0.55);
  }
`;

const GridMesh = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(0, 131, 140, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 131, 140, 0.055) 1px, transparent 1px);
  background-size: 48px 48px;
  animation: ${gridDrift} 10s linear infinite;
  mask-image: radial-gradient(
    ellipse 80% 80% at 50% 50%,
    black 20%,
    transparent 85%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 80% 80% at 50% 50%,
    black 20%,
    transparent 85%
  );
`;

const DotMatrix = styled.div`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  width: 220px;
  height: 220px;
  background-image: radial-gradient(
    circle,
    rgba(0, 131, 140, 0.28) 1.2px,
    transparent 1.2px
  );
  background-size: 18px 18px;

  ${(p) =>
    p.$pos === "top-right" &&
    `
    top: 0; right: 0;
    mask-image: radial-gradient(ellipse at top right, black 0%, transparent 68%);
    -webkit-mask-image: radial-gradient(ellipse at top right, black 0%, transparent 68%);
  `}
  ${(p) =>
    p.$pos === "bottom-left" &&
    `
    bottom: 0; left: 0;
    mask-image: radial-gradient(ellipse at bottom left, black 0%, transparent 68%);
    -webkit-mask-image: radial-gradient(ellipse at bottom left, black 0%, transparent 68%);
  `}
`;

/*

const LightBeam = styled.div`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  top: -10%;
  left: ${(p) => p.$left};
  width: ${(p) => p.$width};
  height: 130%;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 131, 140, ${(p) => p.$opacity}) 30%,
    rgba(0, 200, 180, ${(p) => p.$opacity}) 50%,
    rgba(0, 131, 140, ${(p) => p.$opacity}) 70%,
    transparent 100%
  );
  animation: ${beamSlide} 18s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay};
  filter: blur(0.5px);
`;

*/

const DecorRing = styled.div`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$size};
  height: ${(p) => p.$size};
  border-radius: 50%;
  border: 1px solid rgba(0, 131, 140, 0.18);
  animation: ${ringExpand} 6s ease-out infinite;
  animation-delay: ${(p) => p.$delay};

  &::after {
    content: "";
    position: absolute;
    inset: 20%;
    border-radius: 50%;
    border: 1px solid rgba(0, 131, 140, 0.12);
    animation: ${ringExpand} 6s ease-out infinite;
    animation-delay: calc(${(p) => p.$delay} + 1s);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/*

const AccentLine = styled.div`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  top: 30%;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(0, 131, 140, 0.12) 20%,
    rgba(0, 200, 180, 0.18) 50%,
    rgba(0, 131, 140, 0.12) 80%,
    transparent 100%
  );
`;

*/

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  background: radial-gradient(
    ellipse 100% 100% at 50% 50%,
    transparent 40%,
    rgba(4, 8, 20, 0.55) 100%
  );
`;

const HeroBanner = styled.div`
  position: relative;
  z-index: 4;
  width: 100%;
  height: 100%;
`;
